// M15 actions behind the AI buttons. Each one sends a small rendering of the photo to the provider the user
// chose, only when the user presses the button, and only with a key set (aiClient throws 'no-key' before any
// request otherwise).
import { create } from 'zustand';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import { useEditor } from '../../state/editor';
import { defaultSettings } from '../../model/defaults';
import { exportWithExtras } from '../exportHelpers';
import type { Settings } from '../../model/types';
import { adText, aiImage, smartFix, type SmartFix } from './aiClient';
import { useDesign } from '../../design/state';
import { setAd, imageNodes } from '../../design/templates/instantiate';
import { getAsset } from '../../design/assets';
import { sanitizeName } from '../../io/rename';

type AiUi = {
  busy: null | 'smartFix' | 'touchup' | 'upscale' | 'adText';
  error: string | null;
  /** The last AI image result, shown beside the original (never applied to the photo). */
  compare: null | {
    kind: 'touchup' | 'upscale';
    photoName: string;
    original: string;
    result: string;
    bytes: Uint8Array;
    mime: string;
    width: number;
    height: number;
  };
  settingsOpen: boolean;
};
export const useAiUi = create<AiUi>(() => ({ busy: null, error: null, compare: null, settingsOpen: false }));

const active = (): LibraryPhoto | undefined => {
  const l = useLibrary.getState();
  return l.photos.find((p) => p.id === l.activeId);
};

async function jpegOf(p: LibraryPhoto, s: Settings, edge: number): Promise<Uint8Array> {
  const r = await exportWithExtras(
    p,
    s,
    {
      format: 'jpeg',
      bitDepth: 8,
      quality: 85,
      size: { mode: 'longEdge', w: edge },
      dpi: 72,
      colorSpace: 'srgb',
      sharpen: { target: 'none', amount: 'standard' },
      background: 'asEdited',
      metadata: 'stripAll',
    },
    {},
  );
  return r.bytes;
}

async function guard<T>(busy: NonNullable<AiUi['busy']>, fn: () => Promise<T>): Promise<T | null> {
  useAiUi.setState({ busy, error: null });
  try {
    return await fn();
  } catch (e) {
    useAiUi.setState({ error: (e as Error).message });
    return null;
  } finally {
    useAiUi.setState({ busy: null });
  }
}

/** Applies smart-fix values to settings (absolute values for the sliders it covers). */
export function applySmartFix(s: Settings, f: SmartFix) {
  s.light = {
    exposure: f.exposure,
    contrast: f.contrast,
    highlights: f.highlights,
    shadows: f.shadows,
    whites: f.whites,
    blacks: f.blacks,
  };
  s.wb = { temp: f.temp, tint: f.tint };
  s.color = { vibrance: f.vibrance, saturation: f.saturation };
  s.cleanWhites = { ...s.cleanWhites, amount: f.cleanWhites };
}

/**
 * (a) Smart fix: a ≤ 768 px JPEG of the photo as shot (crop kept, other edits off) → slider values → one undo
 * step. Never generated pixels.
 */
export async function smartFixActive(): Promise<SmartFix | null> {
  const p = active();
  if (!p) return null;
  return guard('smartFix', async () => {
    const cur = useEditor.getState().get(p.id);
    const neutral = { ...defaultSettings(), geometry: cur.geometry, upscale: cur.upscale };
    const fix = await smartFix(await jpegOf(p, neutral, 768));
    useEditor.getState().update(p.id, (s) => applySmartFix(s, fix));
    return fix;
  });
}

/** (c) AI touch-up / upscale of the active photo with its current edits: shown beside the original only. */
export async function aiImageActive(kind: 'touchup' | 'upscale'): Promise<void> {
  const p = active();
  if (!p) return;
  await guard(kind, async () => {
    const s = useEditor.getState().get(p.id);
    const src = await jpegOf(p, s, 1536);
    const out = await aiImage(kind, { bytes: src, mime: 'image/jpeg' });
    const bmp = await createImageBitmap(new Blob([out.bytes as Uint8Array<ArrayBuffer>], { type: out.mime }));
    const prev = useAiUi.getState().compare;
    if (prev) {
      URL.revokeObjectURL(prev.original);
      URL.revokeObjectURL(prev.result);
    }
    useAiUi.setState({
      compare: {
        kind,
        photoName: p.name,
        original: URL.createObjectURL(new Blob([src as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' })),
        result: URL.createObjectURL(new Blob([out.bytes as Uint8Array<ArrayBuffer>], { type: out.mime })),
        bytes: out.bytes,
        mime: out.mime,
        width: bmp.width,
        height: bmp.height,
      },
    });
    bmp.close();
  });
}

/** Saves the AI result as a NEW file (exports/ai/…, or a download); the photo itself is never replaced. */
export async function saveAiResult(): Promise<string | null> {
  const c = useAiUi.getState().compare;
  if (!c) return null;
  const ext = c.mime.includes('png') ? 'png' : 'jpg';
  const name = `${sanitizeName(c.photoName.replace(/\.[^.]+$/, '')) || 'photo'}_ai-${c.kind}.${ext}`;
  const folder = useLibrary.getState().folder;
  if (folder?.mode === 'fsa') return folder.writeExport('ai', name, c.bytes);
  const url = URL.createObjectURL(new Blob([c.bytes as Uint8Array<ArrayBuffer>], { type: c.mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** (b) Ad text: from the ad's photo and the dish name typed so far → the form's fields (one undo step). */
export async function adTextForAd(): Promise<void> {
  const doc = useDesign.getState().doc;
  if (!doc?.ad) return;
  await guard('adText', async () => {
    const slot = imageNodes(doc, 'photo').find((n) => n.assetId);
    const asset = slot?.assetId ? getAsset(slot.assetId) : undefined;
    const r = await adText({
      dishName: doc.ad!.dishName.es || doc.ad!.dishName.en,
      photo: asset ? { bytes: asset.bytes, mime: asset.type } : undefined,
    });
    useDesign.getState().run(
      setAd((a) => {
        a.dishName = r.dishName;
        a.description = r.description;
        a.headline = r.promo;
      }),
    );
    useDesign.getState().endGesture();
  });
}
